import { useNavigate, useParams } from 'react-router-dom';
import { TakeDefined } from '@types';
import { workTemplateAlerts as alerts, useAlert } from 'shared/components/alerts';
import { useUpdateWorkTemplate } from 'ogp/hooks/queryHooks/jobs/use-update-work-template';
import { routes } from 'Routes';
import { useCompanyRelativePath } from 'ogp/hooks';
import { RenderUnsettledUI } from 'shared/components/render-unsettled-ui';
import { Header, Page } from 'redesign';
import { invariant } from 'shared/utils/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { useGetWorkTemplateCreateData } from 'ogp/hooks/queryHooks/jobs/use-get-work-template-create-data';
import { usePageProvider } from 'ogp/layouts';
import { useExternalParams, useSiteDetection } from 'shared/hooks';
import { GetCompanyEmploymentTypes, useGetCompanyEmploymentTypes, useGetWorkTemplate } from 'api';
import { ToolboxForm } from 'shared/components/common/toolbox-form';
import { WorkTemplateForm } from '../../forms/shared/work-template-form';
import {
  decodeWorkTemplateFormToWorkTemplate,
  encodeWorkTemplateToWorkTemplateForm,
} from '../utils';
import { WorkTemplateFormContext } from '../../forms/shared/work-template-form.types';
import { WORK_TEMPLATE_INITIAL_VALUES } from '../../work-templates.types';

export const WorkTemplateEditDataProvider = () => {
  const { id } = useParams<'id'>();
  invariant(id, 'Job ID must be defined');

  const { site } = useSiteDetection();
  const jobQuery = useGetWorkTemplate(site, id, !!id);
  const employmentTypeOfCompanyQuery = useGetCompanyEmploymentTypes(site);
  const workTemplateCreateDataQuery = useGetWorkTemplateCreateData();

  if (workTemplateCreateDataQuery.status !== 'success') {
    return <RenderUnsettledUI data={workTemplateCreateDataQuery} />;
  }

  if (employmentTypeOfCompanyQuery.status !== 'success') {
    return <RenderUnsettledUI data={employmentTypeOfCompanyQuery} />;
  }

  if (jobQuery.status !== 'success') {
    return <RenderUnsettledUI data={jobQuery} />;
  }

  const defaultValues = encodeWorkTemplateToWorkTemplateForm(jobQuery.data);

  return (
    <WorkTemplateEdit
      defaultValues={defaultValues}
      jobCreateData={workTemplateCreateDataQuery.data}
      employmentData={employmentTypeOfCompanyQuery.data}
      id={id}
    />
  );
};

export const WorkTemplateEdit = ({
  defaultValues,
  jobCreateData,
  employmentData,
  id,
}: {
  defaultValues: WorkTemplateFormContext;
  jobCreateData: TakeDefined<ReturnType<typeof useGetWorkTemplateCreateData>['data']>;
  employmentData: GetCompanyEmploymentTypes;
  id: string;
}) => {
  const formMethods = useForm<WorkTemplateFormContext>({
    defaultValues,
  });
  const { alertSuccess, alertError } = useAlert();
  const generatePath = useCompanyRelativePath();
  const [{ filters }] = usePageProvider();
  const navigate = useNavigate();
  const [params] = useExternalParams(WORK_TEMPLATE_INITIAL_VALUES);
  const updateJob = useUpdateWorkTemplate();
  const redirectPath = generatePath(routes.WorkTemplates, undefined, {
    ...filters,
    search: params.search || undefined,
  });
  const handleCancel = () => {
    navigate(redirectPath, { replace: true });
  };

  const handleSubmit = () => {
    formMethods.handleSubmit((data) => {
      const decodedData = decodeWorkTemplateFormToWorkTemplate(data);
      updateJob.mutate(
        {
          ...decodedData,
          maxClaimants: decodedData.maxClaimants ? Number(decodedData.maxClaimants) : null,
          id,
        },
        {
          onSuccess: () => {
            alertSuccess(alerts.success.updated);
            navigate(redirectPath);
          },
          onError: (e) => alertError(e),
        }
      );
    })();
  };

  return (
    <FormProvider {...formMethods}>
      <Page
        header={<Header titleText="Functie aanpassen" />}
        toolbox={
          <ToolboxForm
            onSubmit={handleSubmit}
            mutationStatus={updateJob.status}
            mode="update"
            onCancel={handleCancel}
          />
        }
      >
        <WorkTemplateForm
          formState="update"
          employmentType={employmentData}
          workTemplateCreateData={jobCreateData}
        />
      </Page>
    </FormProvider>
  );
};
